import { Dialog } from '@angular/cdk/dialog';
import { DestroyRef, Directive, inject, input, OnInit } from '@angular/core';
import { gameManager } from 'src/app/game/businesslogic/GameManager';
import { GhsManager } from 'src/app/game/businesslogic/GhsManager';
import { settingsManager } from 'src/app/game/businesslogic/SettingsManager';
import { Character } from 'src/app/game/model/Character';
import { AttackModifierDeck } from 'src/app/game/model/data/AttackModifier';
import { Entity } from 'src/app/game/model/Entity';
import { GameState } from 'src/app/game/model/Game';
import { Monster } from 'src/app/game/model/Monster';
import { ObjectiveContainer } from 'src/app/game/model/ObjectiveContainer';
import { SummonState } from 'src/app/game/model/Summon';
import { EntitiesMenuDialogComponent } from 'src/app/ui/figures/entities-menu/entities-menu-dialog';
import { PartySheetDialogComponent } from 'src/app/ui/figures/party/party-sheet-dialog';
import { ScenarioChartDialogComponent } from 'src/app/ui/figures/party/scenario-chart/scenario-chart';
import { WorldMapComponent } from 'src/app/ui/figures/party/world-map/world-map';
import { FooterComponent } from 'src/app/ui/footer/footer';
import { HeaderComponent } from 'src/app/ui/header/header';
import { KeyboardShortcutsComponent } from 'src/app/ui/header/menu/keyboard-shortcuts/keyboard-shortcuts';
import { ghsDialogClosingHelper, ghsFilterInputFocus, ghsValueSign } from 'src/app/ui/helper/Static';

export type KEYBOARD_SHORTCUT_EVENTS =
  | 'undo'
  | 'zoom'
  | 'fullscreen'
  | 'round'
  | 'am'
  | 'loot'
  | 'active'
  | 'element'
  | 'absent'
  | 'select'
  | 'menu'
  | 'level'
  | 'scenario'
  | 'handSize'
  | 'traits'
  | 'party'
  | 'map'
  | 'chart'
  | 'damageHP'
  | 'activeCharacter'
  | 'playerNumber';

@Directive({
  selector: '[ghs-keyboard-shortcuts]'
})
export class KeyboardShortcuts implements OnInit {
  private dialog = inject(Dialog);
  private ghsManager = inject(GhsManager);

  private destroyRef = inject(DestroyRef);

  readonly inputHeader = input<HeaderComponent>(undefined, { alias: 'header' });
  get header(): HeaderComponent | undefined {
    return this.inputHeader();
  }

  readonly inputFooter = input<FooterComponent>(undefined, { alias: 'footer' });
  get footer(): FooterComponent | undefined {
    return this.inputFooter();
  }

  readonly allowed = input<KEYBOARD_SHORTCUT_EVENTS[]>(['undo', 'zoom', 'menu']);
  zoomInterval: any = null;
  currentZoom: number = 0;
  dialogOpen: boolean = false;
  dialogClosing: boolean = false;
  keydown!: (event: KeyboardEvent) => void;
  keyup!: (event: KeyboardEvent) => void;
  timeout: any;

  constructor() {
    this.ghsManager.uiChangeEffect(() => {
      this.currentZoom = settingsManager.settings.zoom;
    });
    this.dialog.afterOpened.subscribe({
      next: () => {
        this.dialogOpen = true;
        this.dialogClosing = false;
      }
    });
    this.dialog.afterAllClosed.subscribe({
      next: () => {
        this.dialogClosing = true;
        setTimeout(() => {
          if (this.dialogClosing) {
            this.dialogOpen = false;
          }
        }, 250);
      }
    });
  }

  applySelect() {
    const entities = gameManager.entityManager.getIndexedEntities(gameManager.stateManager.keyboardSelecting === 'w');
    const selectedIndex = gameManager.stateManager.keyboardSelect;
    gameManager.stateManager.keyboardSelecting = false;
    gameManager.stateManager.keyboardSelect = -1;
    if (selectedIndex > 0 && selectedIndex <= entities.length) {
      this.dialog.open(EntitiesMenuDialogComponent, {
        panelClass: ['dialog'],
        data: {
          entity: entities[selectedIndex - 1].entity,
          figure: entities[selectedIndex - 1].figure,
          entityIndexKey: true
        }
      });
    }
  }

  ngOnInit(): void {
    this.currentZoom = settingsManager.settings.zoom;

    this.keydown = (event: KeyboardEvent) => {
      const ctrlOrMeta = event.ctrlKey || event.metaKey;
      if (settingsManager.settings.keyboardShortcuts && ghsFilterInputFocus()) {
        const lootDeck = this.footer ? this.footer.lootDeck() : undefined;
        const ghsLevel = this.footer ? this.footer.ghsLevel() : undefined;
        const ghsScenario = this.footer ? this.footer.ghsScenario() : undefined;
        const allowed = this.allowed();
        if (gameManager.stateManager.keyboardSelecting) {
          if (event.key === 'Escape' || event.key === 'Backspace' || event.key === 's' || event.key === 'w') {
            gameManager.stateManager.keyboardSelect = -1;
            gameManager.stateManager.keyboardSelecting = false;
          } else if (event.key === 'Enter') {
            this.applySelect();
          } else if (event.key in ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']) {
            const entities = gameManager.entityManager.getIndexedEntities(gameManager.stateManager.keyboardSelecting === 'w');
            const keyNumber = +event.key;
            if (this.timeout) {
              clearTimeout(this.timeout);
              this.timeout = undefined;

              const combined: number = gameManager.stateManager.keyboardSelect * 10 + keyNumber;
              gameManager.stateManager.keyboardSelect = combined;
              this.applySelect();
            } else if (keyNumber * 10 < entities.length) {
              gameManager.stateManager.keyboardSelect = keyNumber;
              this.timeout = setTimeout(() => {
                this.applySelect();
              }, 1000);
            } else {
              gameManager.stateManager.keyboardSelect = keyNumber;
              this.applySelect();
            }
          }
          event.preventDefault();
          event.stopPropagation();
        } else if (
          this.dialogOpen &&
          ghsFilterInputFocus() &&
          event.key === 'Backspace' &&
          !ctrlOrMeta &&
          !event.altKey &&
          !event.shiftKey
        ) {
          ghsDialogClosingHelper(this.dialog.openDialogs[this.dialog.openDialogs.length - 1]);
          event.preventDefault();
        } else if (
          (!this.dialogOpen || allowed.includes('undo')) &&
          ctrlOrMeta &&
          !event.altKey &&
          !event.shiftKey &&
          event.key.toLowerCase() === 'z'
        ) {
          gameManager.stateManager.undo();
          event.preventDefault();
        } else if (
          ((!this.dialogOpen || allowed.includes('undo')) && ctrlOrMeta && !event.altKey && !event.shiftKey && event.key === 'y') ||
          (ctrlOrMeta && event.shiftKey && event.key.toLowerCase() === 'z')
        ) {
          gameManager.stateManager.redo();
          event.preventDefault();
        } else if (
          (!this.dialogOpen || allowed.includes('zoom')) &&
          !ctrlOrMeta &&
          !event.altKey &&
          !event.shiftKey &&
          !this.zoomInterval &&
          (event.key === 'ArrowUp' || event.key === '+')
        ) {
          this.zoom(-1);
          this.zoomInterval = setInterval(() => {
            this.zoom(-1);
          }, 30);
          event.preventDefault();
        } else if (
          (!this.dialogOpen || allowed.includes('zoom')) &&
          !ctrlOrMeta &&
          !event.altKey &&
          !event.shiftKey &&
          !this.zoomInterval &&
          (event.key === 'ArrowDown' || event.key === '-')
        ) {
          this.zoom(1);
          this.zoomInterval = setInterval(() => {
            this.zoom(1);
          }, 30);
          event.preventDefault();
        } else if (
          (!this.dialogOpen || allowed.includes('zoom')) &&
          ctrlOrMeta &&
          !event.altKey &&
          !event.shiftKey &&
          !this.zoomInterval &&
          event.key.toLowerCase() === 'r'
        ) {
          this.currentZoom = 100;
          settingsManager.setZoom(this.currentZoom);
          event.preventDefault();
        } else if (
          (!this.dialogOpen || allowed.includes('round')) &&
          this.footer &&
          !ctrlOrMeta &&
          !event.altKey &&
          !event.shiftKey &&
          !this.zoomInterval &&
          event.key.toLowerCase() === 'n'
        ) {
          this.footer.next();
        } else if (
          (!this.dialogOpen || allowed.includes('am')) &&
          !ctrlOrMeta &&
          !event.altKey &&
          !event.shiftKey &&
          !this.zoomInterval &&
          gameManager.game.state === GameState.next &&
          (event.key.toLowerCase() === 'm' ||
            (settingsManager.settings.amAdvantage && (event.key.toLowerCase() === 'a' || event.key.toLowerCase() === 'd')))
        ) {
          const activeFigure = gameManager.game.figures.find((figure) => figure.active);
          let deck: AttackModifierDeck | undefined = undefined;
          const state: 'advantage' | 'disadvantage' | undefined =
            settingsManager.settings.amAdvantage && event.key.toLowerCase() === 'a'
              ? 'advantage'
              : settingsManager.settings.amAdvantage && event.key.toLowerCase() === 'd'
                ? 'disadvantage'
                : undefined;

          if (
            !activeFigure ||
            (activeFigure instanceof Monster &&
              ((!activeFigure.isAlly && !activeFigure.isAllied) ||
                (!gameManager.fhRules(true) && !settingsManager.settings.alwaysAllyAttackModifierDeck) ||
                !settingsManager.settings.allyAttackModifierDeck))
          ) {
            gameManager.stateManager.before('updateAttackModifierDeck.draw' + (state ? state : ''), 'monster');
            deck = gameManager.game.monsterAttackModifierDeck;
          } else if (activeFigure instanceof Monster) {
            gameManager.stateManager.before('updateAttackModifierDeck.draw' + (state ? state : ''), 'ally');
            deck = gameManager.game.allyAttackModifierDeck;
          } else if (activeFigure instanceof Character) {
            if (settingsManager.settings.characterAttackModifierDeck) {
              if (
                settingsManager.settings.characterAttackModifierDeckActiveBottom &&
                gameManager.game.figures.find((figure) => figure instanceof Character && figure.attackModifierDeckVisible) !== undefined
              ) {
                const character = gameManager.game.figures.find(
                  (figure) => figure instanceof Character && figure.attackModifierDeckVisible
                ) as Character;
                gameManager.stateManager.before(
                  'updateAttackModifierDeck.draw' + (state ? state : ''),
                  gameManager.characterManager.characterName(character, true, true)
                );
                deck = character.attackModifierDeck;
              } else if (activeFigure.attackModifierDeckVisible) {
                gameManager.stateManager.before(
                  'updateAttackModifierDeck.draw' + (state ? state : ''),
                  gameManager.characterManager.characterName(activeFigure, true, true)
                );
                deck = activeFigure.attackModifierDeck;
              } else {
                activeFigure.attackModifierDeckVisible = true;
              }
            } else {
              gameManager.stateManager.before('updateAttackModifierDeck.draw' + (state ? state : ''), 'monster');
              deck = gameManager.game.monsterAttackModifierDeck;
            }
          } else if (activeFigure instanceof ObjectiveContainer) {
            if (
              activeFigure.amDeck === 'M' ||
              (activeFigure.amDeck === 'A' &&
                ((!gameManager.fhRules(true) && !settingsManager.settings.alwaysAllyAttackModifierDeck) ||
                  !settingsManager.settings.allyAttackModifierDeck))
            ) {
              gameManager.stateManager.before('updateAttackModifierDeck.draw' + (state ? state : ''), 'monster');
              deck = gameManager.game.monsterAttackModifierDeck;
            } else if (
              activeFigure.amDeck === 'A' &&
              settingsManager.settings.allyAttackModifierDeck &&
              (gameManager.fhRules(true) || settingsManager.settings.alwaysAllyAttackModifierDeck)
            ) {
              gameManager.stateManager.before('updateAttackModifierDeck.draw' + (state ? state : ''), 'ally');
              deck = gameManager.game.allyAttackModifierDeck;
            } else if (activeFigure.amDeck) {
              const character = gameManager.game.figures.find(
                (figure) => figure instanceof Character && figure.name === activeFigure.amDeck
              ) as Character;
              if (character && settingsManager.settings.characterAttackModifierDeck) {
                if (character.attackModifierDeckVisible) {
                  gameManager.stateManager.before(
                    'updateAttackModifierDeck.draw' + (state ? state : ''),
                    gameManager.characterManager.characterName(character, true, true)
                  );
                  deck = character.attackModifierDeck;
                } else {
                  character.attackModifierDeckVisible = true;
                }
              } else {
                gameManager.stateManager.before('updateAttackModifierDeck.draw' + (state ? state : ''), 'monster');
                deck = gameManager.game.monsterAttackModifierDeck;
              }
            }
          }

          if (deck) {
            deck.active = true;
            const deckCharacter = gameManager.game.figures.find(
              (figure) => figure instanceof Character && figure.attackModifierDeck === deck
            ) as Character | undefined;
            gameManager.attackModifierManager.drawModifier(deck, state, deckCharacter);
            gameManager.stateManager.after();
          }
          event.preventDefault();
        } else if (
          (!this.dialogOpen || allowed.includes('loot')) &&
          gameManager.game.state === GameState.next &&
          !ctrlOrMeta &&
          !event.altKey &&
          !event.shiftKey &&
          !this.zoomInterval &&
          event.key.toLowerCase() === 'l' &&
          settingsManager.settings.lootDeck &&
          gameManager.game.lootDeck.cards.length > 0 &&
          this.footer &&
          lootDeck
        ) {
          if (!lootDeck.deck.active) {
            this.footer.toggleLootDeck();
          }
          lootDeck.draw(event, true);
          event.preventDefault();
        } else if (!this.dialogOpen && !ctrlOrMeta && !event.altKey && event.key === 'Tab' && gameManager.game.figures.length > 0) {
          if (gameManager.game.state === GameState.next) {
            this.toggleEntity(event.shiftKey);
          } else {
            let focus = true;
            gameManager.game.figures
              .filter((figure) => figure instanceof Character && !figure.absent)
              .forEach((char, index) => {
                const current = document.getElementById('initiative-input-' + index);
                if (document.activeElement === current) {
                  focus = false;
                }
              });
            if (focus) {
              const current = document.getElementById('initiative-input-0');
              if (current) {
                current.focus();
              }
            }
            event.stopPropagation();
          }
          event.preventDefault();
        } else if (
          (!this.dialogOpen || allowed.includes('level')) &&
          this.footer &&
          !ctrlOrMeta &&
          !event.altKey &&
          !event.shiftKey &&
          event.key.toLowerCase() === 'x' &&
          ghsLevel
        ) {
          ghsLevel.open();
        } else if (
          (!this.dialogOpen || allowed.includes('scenario')) &&
          !ctrlOrMeta &&
          !event.altKey &&
          !event.shiftKey &&
          event.key.toLowerCase() === 'e'
        ) {
          if (settingsManager.settings.partySheet || gameManager.characterManager.characterCount(true) > 0) {
            const ghsScenario = this.footer ? this.footer.ghsScenario() : undefined;
            if (gameManager.game.scenario && this.header) {
              this.header.openEventEffects();
            } else if (!gameManager.game.scenario && this.footer && ghsScenario) {
              ghsScenario.open();
            }
          }
        } else if (
          (!this.dialogOpen || allowed.includes('scenario')) &&
          this.footer &&
          !ctrlOrMeta &&
          !event.altKey &&
          !event.shiftKey &&
          event.key.toLowerCase() === 'f'
        ) {
          settingsManager.toggle('fullscreen');
        } else if (
          (!this.dialogOpen || allowed.includes('scenario')) &&
          this.footer &&
          !ctrlOrMeta &&
          !event.altKey &&
          event.shiftKey &&
          event.key.toLowerCase() === 'f' &&
          ghsScenario &&
          gameManager.game.scenario
        ) {
          ghsScenario.open();
        } else if (
          (!this.dialogOpen || allowed.includes('party')) &&
          !ctrlOrMeta &&
          !event.altKey &&
          !event.shiftKey &&
          event.key.toLowerCase() === 'p' &&
          settingsManager.settings.partySheet
        ) {
          this.dialog.open(PartySheetDialogComponent, {
            panelClass: ['dialog-invert'],
            data: { partySheet: true }
          });
          event.stopPropagation();
          event.preventDefault();
        } else if (
          (!this.dialogOpen || allowed.includes('map')) &&
          !ctrlOrMeta &&
          !event.altKey &&
          !event.shiftKey &&
          event.key.toLowerCase() === 'g' &&
          gameManager.game.edition
        ) {
          const editionData = gameManager.editionData.find((editionData) => editionData.edition === gameManager.game.edition);
          if (editionData) {
            if (editionData.worldMap || editionData.extendWorldMap) {
              if (this.dialogOpen) {
                this.dialog.closeAll();
              }
              this.dialog.open(WorldMapComponent, {
                panelClass: ['fullscreen-panel', 'no-dialog-animations'],
                backdropClass: ['fullscreen-backdrop'],
                data: { edition: gameManager.game.edition }
              });
              event.stopPropagation();
              event.preventDefault();
            }
          }
        } else if (
          (!this.dialogOpen || allowed.includes('chart')) &&
          !ctrlOrMeta &&
          !event.altKey &&
          !event.shiftKey &&
          event.key.toLowerCase() === 'c' &&
          gameManager.game.edition
        ) {
          if (this.dialogOpen) {
            this.dialog.closeAll();
          }
          this.dialog.open(ScenarioChartDialogComponent, {
            panelClass: ['fullscreen-panel', 'no-dialog-animations'],
            backdropClass: ['fullscreen-backdrop'],
            data: {
              edition: gameManager.game.edition
            }
          });
          event.stopPropagation();
          event.preventDefault();
        } else if (
          !this.dialogOpen &&
          !ctrlOrMeta &&
          !event.altKey &&
          !event.repeat &&
          this.header &&
          (event.key === 'Escape' || event.key === 'Backspace')
        ) {
          this.header.openMenu();
          event.stopPropagation();
          event.preventDefault();
        } else if (
          (!this.dialogOpen || allowed.includes('element')) &&
          !ctrlOrMeta &&
          !event.altKey &&
          !event.shiftKey &&
          ['1', '2', '3', '4', '5', '6'].includes(event.key)
        ) {
          const index: number = +event.key - 1;
          const element = gameManager.game.elementBoard[index];
          const elementState = gameManager.nextElementState(element, false, true);
          gameManager.stateManager.before('updateElement', 'game.element.' + element.type, 'game.element.state.' + elementState);
          gameManager.applyElementState(element, elementState);
          gameManager.stateManager.after();
          event.preventDefault();
        } else if (
          (!this.dialogOpen || allowed.includes('absent')) &&
          !ctrlOrMeta &&
          !event.altKey &&
          !event.shiftKey &&
          event.key === 'h'
        ) {
          settingsManager.toggle('hideAbsent');
          event.preventDefault();
        } else if (
          (!this.dialogOpen || allowed.includes('handSize')) &&
          !ctrlOrMeta &&
          !event.altKey &&
          event.shiftKey &&
          event.key === 'H'
        ) {
          settingsManager.toggle('characterHandSize');
          event.preventDefault();
        } else if (
          (!this.dialogOpen || allowed.includes('traits')) &&
          !ctrlOrMeta &&
          !event.altKey &&
          !event.shiftKey &&
          event.key === 't'
        ) {
          settingsManager.toggle('characterTraits');
          event.preventDefault();
        } else if ((!this.dialogOpen || allowed.includes('playerNumber')) && !ctrlOrMeta && !event.altKey && event.key === '#') {
          settingsManager.toggle('playerNumber');
          event.preventDefault();
        } else if (
          (!this.dialogOpen || allowed.includes('damageHP')) &&
          !ctrlOrMeta &&
          !event.altKey &&
          !event.shiftKey &&
          event.key === 'i'
        ) {
          settingsManager.toggle('damageHP');
          event.preventDefault();
        } else if (
          (!this.dialogOpen || allowed.includes('select')) &&
          !ctrlOrMeta &&
          !event.altKey &&
          !event.shiftKey &&
          (event.key === 's' || event.key === 'w')
        ) {
          gameManager.stateManager.keyboardSelecting = event.key;
          this.ghsManager.triggerUiChange();
        } else if (
          !this.dialogOpen &&
          !ctrlOrMeta &&
          !event.altKey &&
          !event.shiftKey &&
          !event.repeat &&
          !gameManager.stateManager.keyboardSelecting &&
          event.key === 'Enter'
        ) {
          const activeFigure = gameManager.game.figures.find((figure) => figure.active);
          if (activeFigure) {
            let activeEntity: Entity | undefined;
            if (activeFigure instanceof Character) {
              activeEntity =
                (settingsManager.settings.activeSummons && activeFigure.summons.find((summon) => summon.active)) || activeFigure;
            } else if (activeFigure instanceof Monster) {
              activeEntity =
                (settingsManager.settings.activeStandees && activeFigure.entities.find((entity) => entity.active)) || undefined;
            } else if (activeFigure instanceof ObjectiveContainer) {
              activeEntity = activeFigure.entities.find((entity) => entity.active);
            }
            if (activeEntity) {
              this.dialog.open(EntitiesMenuDialogComponent, {
                panelClass: ['dialog'],
                data: { entity: activeEntity, figure: activeFigure, entityIndexKey: false }
              });
              event.preventDefault();
            }
          }
        } else if (!this.dialogOpen && !ctrlOrMeta && !event.altKey && event.key === '?') {
          this.dialog.open(KeyboardShortcutsComponent, {
            panelClass: ['dialog']
          });
          event.preventDefault();
        } else if (!this.dialogOpen && !ctrlOrMeta && (event.altKey || event.shiftKey) && event.code === 'KeyX') {
          const character = gameManager.game.figures.find((figure) => figure instanceof Character && figure.active) as Character;
          if (character) {
            if (event.altKey) {
              gameManager.entityManager.before(character, character, 'changeXP', ghsValueSign(1));
              character.experience += 1;
              gameManager.stateManager.after();
            } else if (event.shiftKey) {
              gameManager.entityManager.before(character, character, 'changeXP', ghsValueSign(-1));
              character.experience -= 1;
              if (character.experience < 0) {
                character.experience = 0;
              }
              gameManager.stateManager.after();
            }
          }
        } else if (!this.dialogOpen && !ctrlOrMeta && (event.key === 'l' || (event.shiftKey && event.key === 'L'))) {
          const character = gameManager.game.figures.find((figure) => figure instanceof Character && figure.active) as Character;
          if (character && !gameManager.fhRules()) {
            switch (event.key) {
              case 'l':
                gameManager.entityManager.before(character, character, 'changeLoot', ghsValueSign(1));
                character.loot += 1;
                gameManager.stateManager.after();
                break;
              case 'L':
                gameManager.entityManager.before(character, character, 'changeLoot', ghsValueSign(-1));
                character.loot -= 1;
                if (character.loot < 0) {
                  character.loot = 0;
                }
                gameManager.stateManager.after();
                break;
            }
          }
        }
      }
    };
    window.addEventListener('keydown', this.keydown);

    this.keyup = (event: KeyboardEvent) => {
      if (
        settingsManager.settings.keyboardShortcuts &&
        ghsFilterInputFocus() &&
        this.zoomInterval &&
        (event.key === 'ArrowUp' || event.key === '+' || event.key === 'ArrowDown' || event.key === '-')
      ) {
        clearInterval(this.zoomInterval);
        this.zoomInterval = null;
        if (settingsManager.settings.zoom !== this.currentZoom) {
          settingsManager.setZoom(this.currentZoom);
        }
        event.preventDefault();
      }
    };
    window.addEventListener('keyup', this.keyup);

    this.destroyRef.onDestroy(() => {
      window.removeEventListener('keydown', this.keydown);
      window.removeEventListener('keyup', this.keyup);
    });
  }

  zoom(value: number) {
    this.currentZoom = settingsManager.applyZoom(this.currentZoom + value, value);
  }

  toggleEntity(reverse: boolean) {
    const figures = gameManager.game.figures.filter((figure) => gameManager.gameplayFigure(figure));
    let activeFigure = figures.find((figure) => figure.active);

    if (!activeFigure && reverse) {
      activeFigure = figures[figures.length - 1];
    } else if (activeFigure && reverse && figures.indexOf(activeFigure) > 0) {
      if (activeFigure instanceof Character) {
        const summons = activeFigure.summons.filter(
          (summon) => gameManager.entityManager.isAlive(summon) && summon.state !== SummonState.new
        );
        const activeSummon = summons.find((summon) => summon.active);
        if (!activeSummon || (activeSummon && summons.indexOf(activeSummon) === 0)) {
          activeFigure.summons.forEach((summon) => (summon.active = false));
          activeFigure = figures[figures.indexOf(activeFigure) - 1];
        }
      } else {
        activeFigure = figures[figures.indexOf(activeFigure) - 1];
      }
    }

    if (activeFigure) {
      if (activeFigure instanceof Character) {
        const activeSummon = activeFigure.summons.find((summon) => summon.active);
        const summonsAfterTurn = activeFigure.summons.filter((summon) => summon.afterTurn);
        if (
          settingsManager.settings.activeSummons &&
          !activeSummon &&
          activeFigure.active &&
          summonsAfterTurn.length &&
          !summonsAfterTurn.find((summon) => summon.active)
        ) {
          gameManager.stateManager.before(
            'summonInactive',
            gameManager.characterManager.characterName(activeFigure, true, true),
            'data.summon.' + summonsAfterTurn[0].name
          );
          summonsAfterTurn.forEach((spirit) => (spirit.afterTurnActive = true));
        } else if (settingsManager.settings.activeSummons && activeFigure.active && activeSummon) {
          gameManager.stateManager.before(
            'summonInactive',
            gameManager.characterManager.characterName(activeFigure, true, true),
            'data.summon.' + activeSummon.name
          );
        } else {
          gameManager.stateManager.before(
            activeFigure.active ? 'unsetActive' : 'setActive',
            gameManager.characterManager.characterName(activeFigure, true, true)
          );
        }
        gameManager.roundManager.toggleFigure(activeFigure);
        gameManager.stateManager.after();
      } else if (activeFigure instanceof Monster) {
        let toggleFigure = true;
        const entities = activeFigure.entities
          .filter((entity) => gameManager.entityManager.isAlive(entity) && entity.summon !== SummonState.new)
          .sort(gameManager.monsterManager.sortEntities);
        if (settingsManager.settings.activeStandees) {
          let activeEntity = entities.find((entity) => entity.active);
          if (!activeEntity && entities.length > 0 && reverse && activeFigure.active) {
            activeEntity = entities[entities.length - 1];
            gameManager.stateManager.before(
              activeEntity ? 'unsetEntityActive' : 'setEntityActive',
              'data.monster.' + activeFigure.name,
              'monster.' + activeEntity.type,
              activeEntity.number
            );
            gameManager.monsterManager.toggleActive(activeFigure, activeEntity);
            activeEntity.active = true;
            toggleFigure = false;
            gameManager.stateManager.after();
          } else if (activeEntity && !reverse) {
            gameManager.stateManager.before(
              activeEntity ? 'unsetEntityActive' : 'setEntityActive',
              'data.monster.' + activeFigure.name,
              'monster.' + activeEntity.type,
              activeEntity.number
            );
            gameManager.monsterManager.toggleActive(activeFigure, activeEntity);
            const entityIndex = entities.indexOf(activeEntity);
            if (entityIndex < entities.length - 1) {
              let offset = 1;
              while (entityIndex + offset < entities.length && entities[entityIndex + offset].off) {
                offset++;
              }
              if (entityIndex + offset < entities.length) {
                entities[entityIndex + offset].active = true;
              }
            }
            toggleFigure = false;
            gameManager.stateManager.after();
          }
        }
        if (toggleFigure) {
          gameManager.stateManager.before(activeFigure.active ? 'unsetActive' : 'setActive', 'data.monster.' + activeFigure.name);
          if (reverse && !activeFigure.active && settingsManager.settings.activeStandees) {
            activeFigure.entities.forEach((entity) => {
              if (gameManager.entityManager.isAlive(entity)) {
                entity.active = true;
                entity.off = false;
              }
            });
          }
          gameManager.roundManager.toggleFigure(activeFigure);
          gameManager.stateManager.after();
        }
      } else if (activeFigure instanceof ObjectiveContainer) {
        gameManager.stateManager.before(activeFigure.active ? 'unsetActive' : 'setActive', activeFigure.title || activeFigure.name);
        gameManager.roundManager.toggleFigure(activeFigure);
        gameManager.stateManager.after();
      }

      const roundButton = document.querySelector('.round-container');
      if (roundButton) {
        if (figures.find((figure) => figure.active) === undefined) {
          (roundButton as HTMLElement).focus();
        } else {
          (roundButton as HTMLElement).blur();
        }
      }
    }
  }
}
